import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Image as ImageIcon,
  MapPin,
  Phone,
  Globe,
  Upload,
  Sparkles,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import Logo from '@/components/Logo';
import { frictionlessApi, MenuData, MenuTypeInfo } from '@/services/api/frictionlessApi';
import { frictionlessTempApi } from '@/services/api/frictionlessTempApi';

interface MenuSelection {
  menu: MenuData;
  selected: boolean;
  menu_type: string;
  description: string;
  is_seasonal: boolean;
  seasonal_start?: string;
  seasonal_end?: string;
}

// Helper function to validate and sanitize URLs to prevent XSS attacks
const sanitizeUrl = (url: string): string => {
  if (!url) return '#';
  
  // Remove any whitespace
  const trimmedUrl = url.trim();
  
  // Check for dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = trimmedUrl.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return '#'; // Return safe fallback
    }
  }
  
  // Add https:// if no protocol is specified
  if (!trimmedUrl.match(/^https?:\/\//i)) {
    return `https://${trimmedUrl}`;
  }
  
  return trimmedUrl;
};

const MenuSearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const searchResults = location.state?.searchResults;
  const restaurantName = location.state?.restaurantName;
  const searchLocation = location.state?.location;
  
  const [menuSelections, setMenuSelections] = useState<MenuSelection[]>([]);
  const [menuTypes, setMenuTypes] = useState<MenuTypeInfo[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!searchResults) {
      navigate('/restaurant-menu-search');
      return;
    }

    // Check if no menus were found - redirect to fallback upload page
    if (!searchResults.menus || searchResults.menus.length === 0) {
      // Redirect to manual upload with a helpful message
      navigate('/restaurant-menu-upload-fallback', {
        state: {
          restaurant: searchResults.restaurant || { name: restaurantName },
          searchQuery: restaurantName,
          message: 'No menu PDFs found online'
        }
      });
      return;
    }

    // Initialize menu selections - only select the first menu
    const selections = searchResults.menus.map((menu: MenuData, index: number) => ({
      menu,
      selected: index === 0, // Only select the first menu
      menu_type: menu.menu_type,
      description: menu.suggested_description,
      is_seasonal: menu.is_seasonal,
      seasonal_start: '',
      seasonal_end: ''
    }));
    setMenuSelections(selections);

    // Load menu types
    loadMenuTypes();
  }, [searchResults, navigate]);

  const loadMenuTypes = async () => {
    try {
      const types = await frictionlessApi.getSupportedMenuTypes();
      setMenuTypes(types);
    } catch (error) {
      console.error('Failed to load menu types:', error);
    }
  };

  const updateSelection = (index: number, updates: Partial<MenuSelection>) => {
    setMenuSelections(prev => {
      const newSelections = [...prev];
      
      // If selecting a menu, unselect all others (only allow ONE menu)
      if (updates.selected === true) {
        // Unselect all menus first
        newSelections.forEach((selection, i) => {
          if (i !== index) {
            selection.selected = false;
          }
        });
      }
      
      newSelections[index] = { ...newSelections[index], ...updates };
      return newSelections;
    });
  };

  const handleContinue = async () => {
    const selectedMenus = menuSelections.filter(s => s.selected);
    
    if (selectedMenus.length === 0) {
      toast({
        title: 'No menus selected',
        description: 'Please select at least one menu to import',
        variant: 'destructive'
      });
      return;
    }

    // Store the menu selection data for the processing page
    const menuData = {
      restaurant_name: restaurantName,
      location: searchLocation,
      restaurant_info: restaurant,
      menus: selectedMenus.map(s => ({
        source_url: s.menu.source_url,
        menu_type: s.menu_type,
        description: s.description,
        is_seasonal: s.is_seasonal,
        seasonal_config: s.is_seasonal ? {
          start_date: s.seasonal_start || '',
          end_date: s.seasonal_end || ''
        } : undefined
      }))
    };

    // Store in sessionStorage for the processing page to retrieve
    sessionStorage.setItem('pending_menu_extraction', JSON.stringify(menuData));
    sessionStorage.setItem('extraction_source', 'frictionless');
    
    // Store restaurant info separately for pre-populating signup form
    sessionStorage.setItem('restaurant_prefill_data', JSON.stringify({
      name: restaurant.name,
      address: restaurant.address,
      phone: restaurant.phone,
      cuisine_type: restaurant.cuisine_type,
      website: restaurant.website,
      rating: restaurant.rating,
      price_level: restaurant.price_level
    }));
    
    // Navigate immediately to the processing page with animated steps
    navigate('/restaurant-menu-processing', {
      state: {
        source: 'frictionless',
        preview_mode: true,
        restaurant_name: restaurantName,
        menu_count: selectedMenus.length
      }
    });
  };

  const handleManualUpload = () => {
    // Simple navigation to upload page with restaurant info
    navigate('/restaurant-menu-upload', {
      state: {
        restaurant: searchResults.restaurant,
        source: 'menu_search_results'
      }
    });
  };

  if (!searchResults) {
    return null;
  }

  const { restaurant, menus } = searchResults;

  return (
    <>
      <style>{`
        /* Modern date picker styling */
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
          opacity: 0.6;
          cursor: pointer;
        }
        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          opacity: 1;
        }
        input[type="date"]::-webkit-datetime-edit {
          padding: 0;
        }
        input[type="date"]::-webkit-datetime-edit-fields-wrapper {
          padding: 0;
        }
        /* Ensure calendar dropdown doesn't get cut off */
        .date-picker-container {
          position: relative;
          z-index: 50;
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-b from-dineai-blue/95 to-dineai-blue">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-center mb-8">
          <Logo />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto"
        >
          {/* Restaurant Info Card */}
          <Card className="bg-white/10 backdrop-blur-md border-white/20 p-6 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-white">
                    {restaurant.name}
                  </h1>
                  {restaurant.address && (
                    <div className="flex items-center gap-2 text-white/80">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm md:text-base">{restaurant.address}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-1 text-white/80">
                  {restaurant.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span>{restaurant.phone}</span>
                    </div>
                  )}
                  {restaurant.website && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      <a href={sanitizeUrl(restaurant.website)} target="_blank" rel="noopener noreferrer" 
                         className="hover:text-dineai-yellow">
                        Website
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                {restaurant.cuisine_type && (
                  <Badge className="bg-dineai-yellow/20 text-dineai-yellow border-dineai-yellow/30">
                    {restaurant.cuisine_type}
                  </Badge>
                )}
                {restaurant.rating && (
                  <div className="text-white/80 mt-2">
                    Rating: {restaurant.rating} ⭐
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Menus Found Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">
              We found {menus.length} menu{menus.length !== 1 ? 's' : ''} for your restaurant
            </h2>
            
            {/* Info about single menu selection */}
            {menus.length > 1 && (
              <p className="text-white/90 text-sm flex items-center gap-2 mb-4">
                <span className="text-blue-400">ℹ️</span>
                Please select ONE menu to get started. You can add more menus after signup.
              </p>
            )}

            {/* Multiple Menus Warning */}
            {searchResults.has_location_duplicates && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-400">⚠️</span>
                  <div className="text-white/90">
                    <p className="text-sm">
                      <span className="font-semibold">Multiple menus found</span> - Please select the menu for your specific location to ensure accurate recommendations.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {menuSelections.map((selection, index) => {
                const menu = selection.menu;

                return (
                  <Card key={index} className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden">
                    <div className="p-6">
                      {/* Menu Header with Thumbnail */}
                      <div className="flex items-start gap-4">
                        <Checkbox
                          checked={selection.selected}
                          onCheckedChange={(checked) => 
                            updateSelection(index, { selected: checked as boolean })
                          }
                          className="mt-1 border-white/50 data-[state=checked]:bg-dineai-yellow data-[state=checked]:border-dineai-yellow"
                        />
                        
                        {/* Thumbnail Preview with Click to View */}
                        <div 
                          className="w-20 h-20 bg-white/10 rounded-lg overflow-hidden border border-white/20 flex-shrink-0 cursor-pointer hover:border-dineai-yellow/50 transition-colors group relative"
                          onClick={() => window.open(sanitizeUrl(menu.source_url), '_blank')}
                          title="Click to view full menu"
                        >
                          {menu.source_type === 'pdf' && menu.source_url ? (
                            // Try to show PDF preview using Google Docs viewer in an iframe
                            <div className="w-full h-full relative bg-white">
                              <iframe
                                src={`https://docs.google.com/viewer?url=${encodeURIComponent(sanitizeUrl(menu.source_url))}&embedded=true`}
                                className="w-full h-full pointer-events-none"
                                style={{ transform: 'scale(0.25)', transformOrigin: 'top left', width: '400%', height: '400%' }}
                                title="PDF preview"
                                onError={(e) => {
                                  // Fallback to icon if iframe fails
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-white/10"><svg class="w-8 h-8 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg></div>';
                                  }
                                }}
                              />
                            </div>
                          ) : menu.file_type === 'image' && menu.thumbnail_url ? (
                            <img 
                              src={sanitizeUrl(menu.thumbnail_url)} 
                              alt="Menu preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {menu.source_type === 'pdf' ? (
                                <FileText className="w-8 h-8 text-white/40" />
                              ) : (
                                <ImageIcon className="w-8 h-8 text-white/40" />
                              )}
                            </div>
                          )}
                          {/* Overlay on hover */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Eye className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {menu.source_type === 'pdf' ? (
                              <FileText className="w-5 h-5 text-dineai-yellow" />
                            ) : (
                              <ImageIcon className="w-5 h-5 text-dineai-yellow" />
                            )}
                            <span className="font-semibold text-white">
                              {selection.description}
                            </span>
                            {menu.type_confidence > 0.8 && (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                High Confidence
                              </Badge>
                            )}
                          </div>

                          {/* Quick Edit Controls */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="text-white/70 text-sm mb-1 block">Menu Type</label>
                              <Select
                                value={selection.menu_type}
                                onValueChange={(value) => updateSelection(index, { menu_type: value })}
                              >
                                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {menuTypes.map(type => (
                                    <SelectItem key={type.type_code} value={type.type_code}>
                                      {type.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="text-white/70 text-sm mb-1 block">Description</label>
                              <Input
                                value={selection.description}
                                onChange={(e) => updateSelection(index, { description: e.target.value })}
                                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                                placeholder="Menu description"
                              />
                            </div>
                          </div>

                          {/* Seasonal Toggle */}
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={selection.is_seasonal}
                                onCheckedChange={(checked) => updateSelection(index, { is_seasonal: checked })}
                                className="data-[state=checked]:bg-dineai-yellow data-[state=unchecked]:bg-white/20"
                              />
                              <label className="text-white/80 text-sm cursor-pointer" 
                                     onClick={() => updateSelection(index, { is_seasonal: !selection.is_seasonal })}>
                                Seasonal Menu
                              </label>
                            </div>

                            {selection.is_seasonal && (
                              <div className="flex flex-wrap gap-2">
                                <div className="relative">
                                  <Input
                                    type="date"
                                    value={selection.seasonal_start}
                                    onChange={(e) => updateSelection(index, { seasonal_start: e.target.value })}
                                    className="bg-white/10 border-white/20 text-white pl-3 pr-10 h-10 rounded-lg hover:bg-white/20 focus:bg-white/20 transition-colors [color-scheme:dark]"
                                    style={{ minWidth: '150px' }}
                                  />
                                  <span className="absolute top-1/2 right-3 -translate-y-1/2 text-white/40 text-xs pointer-events-none">Start</span>
                                </div>
                                <div className="relative">
                                  <Input
                                    type="date"
                                    value={selection.seasonal_end}
                                    onChange={(e) => updateSelection(index, { seasonal_end: e.target.value })}
                                    className="bg-white/10 border-white/20 text-white pl-3 pr-10 h-10 rounded-lg hover:bg-white/20 focus:bg-white/20 transition-colors [color-scheme:dark]"
                                    style={{ minWidth: '150px' }}
                                  />
                                  <span className="absolute top-1/2 right-3 -translate-y-1/2 text-white/40 text-xs pointer-events-none">End</span>
                                </div>
                              </div>
                            )}
                          </div>

                        </div>
                      </div>

                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleContinue}
              disabled={isProcessing || !menuSelections.some(s => s.selected)}
              className="flex-1 bg-dineai-yellow hover:bg-yellow-500 text-black font-semibold h-12 text-lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Continue with Selected Menu
            </Button>

            <Button
              onClick={handleManualUpload}
              variant="outline"
              className="flex-1 bg-white/20 hover:bg-white/30 text-white border border-white/30 font-semibold h-12 text-lg"
            >
              <Upload className="w-5 h-5 mr-2" />
              Upload Different Menu
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default MenuSearchResults;